import React from 'react'
import PropTypes from 'prop-types'
import Lottie from 'react-lottie'
import {css} from '@emotion/core'
import {createMarkup, convertDatoCmsImgPathsToLocal} from '../graphql/datoCmsDataFactory'
import {themes} from '../util/theme'

const Hero = ({
    title,
    subTitle,
    body,
    imageUrl,
    lottieOptions,
    theme,
    themeStyles,
    overrideStyles = ``,
    maskGradient = false,
}) => {
    const {
        primaryTextColor,
        emphasisTextColor,
        lighterBackgroundColor,
        heroGradientFirst,
        heroGradientLast,
        heroMaskColor,
    } = themeStyles

    const heroImage = () => {
        if (!imageUrl) return <div />
        return (
            <div
                className="image"
                css={css`
                    position: relative;
                    width: 100%;
                    @media (max-width: 900px) {
                        padding-top: 10px;
                    }
                `}
            >
                <div
                    className="hero-mask-mobile"
                    css={css`

                        background: ${
                            maskGradient
                                ? `linear-gradient(180deg, ${heroGradientFirst} 0%, ${heroGradientLast} 100%)`
                                : heroMaskColor
                        };
                        height: 100%;
                        width: 76%;
                        position: absolute;
                        right: -50px;
                        bottom 0;
                        display: none;
                        @media (max-width: 900px) {
                            display: block;
                        }
                    `}
                />
                <img
                    src={convertDatoCmsImgPathsToLocal(imageUrl)}
                    alt="hero"
                    css={css`
                        width: 100%;
                        max-width: 650px;
                        display: block;
                        margin: auto;
                        position: relative;
                    `}
                />
            </div>
        )
    }

    const lottieImage = (options) => {
        return (
            <div
                css={css`
                    width: 100%;
                    height: 100%;
                    max-width: 650px;
                    margin: auto;
                `}
            >
                <Lottie
                    css={css`
                        width: 100%;
                        height: 100%;
                    `}
                    options={options}
                />
            </div>
        )
    }

    return (
        <section
            className="hero"
            css={css`
                margin: 5rem 0 0;
                position: relative;
                overflow: hidden;
                background: ${lighterBackgroundColor};
                ${overrideStyles}
                padding: 4rem 2rem 3rem;
                @media (max-width: 900px) {
                    padding: 4rem 2rem 3rem;
                }
            `}
        >
            <div
                className="hero-mask"
                css={css`
                    background: ${maskGradient
                        ? `linear-gradient(180deg, ${heroGradientFirst} 0%, ${heroGradientLast} 100%)`
                        : heroMaskColor};
                    height: 100%;
                    width: 35%;
                    position: absolute;
                    right: 0;
                    top: 0;
                    @media (max-width: 900px) {
                        display: none;
                    }
                `}
            />
            <div
                className="content"
                css={css`
                    max-width: 1200px;
                    margin: auto;
                    display: ${(imageUrl || lottieOptions) && 'flex'};
                    align-items: center;
                    justify-content: space-between;
                    position: relative;
                    @media (max-width: 900px) {
                        flex-direction: column;
                    }
                `}
            >
                <div
                    className="text"
                    css={css`
                        flex: 1;
                        @media (max-width: 900px) {
                            padding: 0;
                        }
                        color: ${primaryTextColor};
                        h2,
                        p,
                        span,
                        em {
                            color: ${primaryTextColor};
                        }
                        h2 {
                            font-size: 40px;
                            margin: 1.5rem 0;
                            font-weight: 600;
                            text-align: ${!(imageUrl || lottieOptions) ? 'center' : 'left'};
                        }
                        h1 {
                            color: ${emphasisTextColor};
                            font-weight: 500;
                            font-size: 20px;
                        }
                        h1,
                        h2 {
                            @media (max-width: 900px) {
                                text-align: center;
                            }
                        }
                    `}
                >
                    <h1>{subTitle}</h1>
                    <h2>{title}</h2>
                    <div
                        css={css`
                            flex: 1;
                            display: none;
                            margin: 0 0 3rem;
                            @media (max-width: 900px) {
                                display: block;
                            }
                        `}
                    >
                        {lottieOptions ? lottieImage(lottieOptions) : heroImage()}
                    </div>
                    <div
                        dangerouslySetInnerHTML={createMarkup(body)}
                        css={css`
                            margin-bottom: 1rem;
                            font-size: 1rem;
                            line-height: 24px;
                            color: ${primaryTextColor};
                            p {
                                margin-bottom: 1rem;
                                font-size: 1rem;
                                line-height: 24px;
                            }
                            strong {
                                color: ${primaryTextColor};
                                font-weight: 600;
                            }
                        `}
                    />
                </div>
                <div
                    css={css`
                        flex: 1;
                        @media (max-width: 900px) {
                            display: none;
                        }
                    `}
                >
                    {lottieOptions ? lottieImage(lottieOptions) : heroImage()}
                </div>
            </div>
        </section>
    )
}

Hero.propTypes = {
    title: PropTypes.string,
    body: PropTypes.string,
    imageUrl: PropTypes.string,
    theme: PropTypes.string,
    themeStyles: PropTypes.object,
}

Hero.defaultProps = {
    title: `Title`,
    body: ``,
    theme: `light`,
    themeStyles: themes.light,
}

export default Hero
